import { SearchOptions, SearchResponse } from '@algolia/client-search';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import {
  LocationPoleEmploiValue,
  SearchEngineFieldLocationPoleEmploi,
} from '@domains/search-engine/entities/location-search-pole-emploi-field';
import { SearchEngineQueryParams } from '@domains/search-engine/search-engine-field';
import { PoleEmploiCity } from '@models/pole-emploi/city';
import { PoleEmploiDepartment } from '@models/pole-emploi/department';
import { PoleEmploiRegion } from '@models/pole-emploi/region';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SearchCommunesWebservice } from '@webservices/search/search-communes.webservice';
import { SearchDepartementsWebservice } from '@webservices/search/search-departements.webservice';
import { SearchRegionsWebservice } from '@webservices/search/search-regions.webservice';
import { NgUpperFirstPipeModule } from '@wizbii/angular-pipes';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, shareReplay, switchMap } from 'rxjs/operators';

type SelectedWorkplace = PoleEmploiCity | PoleEmploiDepartment | PoleEmploiRegion;

enum WorkplaceTypeEnum {
  city = 'commune',
  department = 'departement',
  region = 'region',
}

interface DistanceSearch {
  libelle: string;
  value: number;
}

@UntilDestroy()
@Component({
  selector: 'app-location-search-pole-emploi',
  templateUrl: './location-search-pole-emploi.component.html',
  styleUrls: ['./location-search-pole-emploi.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationSearchPoleEmploiComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationSearchPoleEmploiComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() field!: SearchEngineFieldLocationPoleEmploi;
  @Input() modalOpen!: boolean;
  @Input() liveSearch!: boolean;

  @Output() hasChanged = new EventEmitter<boolean>();

  locationForm: FormGroup = new FormGroup({});
  locationIsFocused = false;

  private _value?: LocationPoleEmploiValue;

  WorkplaceTypeEnum = WorkplaceTypeEnum;

  cities$ = new Observable<SearchResponse<PoleEmploiCity>>();
  departments$ = new Observable<SearchResponse<PoleEmploiDepartment>>();
  regions$ = new Observable<SearchResponse<PoleEmploiRegion>>();
  error$ = new BehaviorSubject<boolean>(false);
  distinctLocationChanges$!: Observable<string>;

  readonly distanceSearch: DistanceSearch[] = [
    { libelle: 'Lieu exact', value: 5 },
    { libelle: '10 km', value: 10 },
    { libelle: '20 km', value: 20 },
    { libelle: '30 km', value: 30 },
    { libelle: '40 km', value: 40 },
    { libelle: '50 km', value: 50 },
    { libelle: '60 km', value: 60 },
    { libelle: '70 km', value: 70 },
    { libelle: '80 km', value: 80 },
    { libelle: '90 km', value: 90 },
    { libelle: '100 km', value: 100 },
  ];

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  set value(val: LocationPoleEmploiValue | undefined) {
    if (this._value === val) {
      return;
    }
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  get value(): LocationPoleEmploiValue | undefined {
    return this._value;
  }

  get location(): FormControl {
    return this.locationForm.get('location') as FormControl;
  }

  get distance(): FormControl {
    return this.locationForm.get('distance') as FormControl;
  }

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly searchCommunesWebservice: SearchCommunesWebservice,
    private readonly searchDepartementsWebservice: SearchDepartementsWebservice,
    private readonly searchRegionsWebservice: SearchRegionsWebservice,
    private readonly route: ActivatedRoute,
    private readonly formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.locationForm = this.formBuilder.group({
      location: undefined,
      distance: undefined,
    });

    combineLatest([
      this.getSelectedLocation(),
      this.route.queryParamMap.pipe(map((q) => q.get(SearchEngineQueryParams.Distance))),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([location, distance]) => {
        this.location.setValue(location);
        this.distance.setValue(distance ? parseInt(distance, 10) : this.distanceSearch[0].value);
      });

    this.distinctLocationChanges$ = this.location.valueChanges.pipe(
      debounceTime(300),
      untilDestroyed(this),
      filter((query) => query && query.length > 0),
      distinctUntilChanged()
    );

    this.location.valueChanges
      .pipe(
        untilDestroyed(this),
        filter((val) => !!val?.workplace)
      )
      .subscribe((val) => {
        this.value = {
          name: val.type,
          code: val.workplace.code,
          distance: this.distance.value ?? '0',
        };

        if (this.liveSearch) {
          this.hasChanged.emit();
        }
      });

    this.distance.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe((val: string) => {
      if (this.value) {
        this.value = {
          ...this.value,
          distance: parseInt(val ?? '0', 10),
        };

        if (this.liveSearch) {
          this.hasChanged.emit();
        }
      }
    });

    this.cities$ = this.getCities();

    if (this.field.hasRegionAndDepartment) {
      this.departments$ = this.getDepartments();
      this.regions$ = this.getRegions();

      combineLatest([
        this.cities$.pipe(map((cities) => cities.hits)),
        this.departments$.pipe(map((departements) => departements.hits)),
        this.regions$.pipe(map((regions) => regions.hits)),
      ])
        .pipe(untilDestroyed(this))
        .subscribe({
          next: ([cities, departements, regions]) => {
            const noLocation = cities?.length === 0 && departements?.length === 0 && regions?.length === 0;
            this.error$.next(noLocation);
          },
        });
    } else {
      this.cities$.pipe(untilDestroyed(this)).subscribe({
        next: (cities) => {
          this.error$.next(cities?.hits.length === 0);
        },
      });
    }
  }

  onClose(): void {
    if (this.value) {
      return;
    }

    this.cities$
      .pipe(
        first(),
        map((cities) => cities.hits[0]),
        filter((city) => !!city)
      )
      .subscribe({
        next: (city) => {
          this.location.setValue({ workplace: city, type: WorkplaceTypeEnum.city });
        },
      });

    if (this.liveSearch) {
      this.hasChanged.emit();
    }
  }

  clearValue(event: any): void {
    this.location.setValue(undefined);
    this.value = undefined;
    this.error$.next(false);

    if (this.liveSearch) {
      this.hasChanged.emit();
    }

    event.stopPropagation();
  }

  trackByDistanceSearch(_: number, distanceSearch: DistanceSearch): number {
    return distanceSearch.value;
  }

  displayFn(value: { workplace: SelectedWorkplace; type: WorkplaceTypeEnum }): string {
    return value?.workplace ? value?.workplace.libelle : '';
  }

  onChange: (val?: LocationPoleEmploiValue) => void = () => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  writeValue(val?: LocationPoleEmploiValue): void {
    if (val) {
      this.value = val;
    }
  }

  registerOnChange(fn: (val?: LocationPoleEmploiValue) => void): void {
    this.onChange = (val) => {
      fn(val);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  trackByPoleEmploiCity(_: number, city: PoleEmploiCity): string {
    return city.code;
  }

  trackByPoleEmploiDepartment(_: number, department: PoleEmploiDepartment): string {
    return department.libelle;
  }

  trackByPoleEmploiRegion(_: number, region: PoleEmploiRegion): string {
    return region.code;
  }

  ngOnDestroy(): void {
    this.error$.complete();
  }

  private getCities(): Observable<SearchResponse<PoleEmploiCity>> {
    return this.distinctLocationChanges$.pipe(
      switchMap((query) => this.searchCommunesWebservice.getCommunes(query)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private getDepartments(): Observable<SearchResponse<PoleEmploiDepartment>> {
    return this.distinctLocationChanges$.pipe(
      switchMap((query) => this.searchDepartementsWebservice.getDepartments(query)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private getRegions(): Observable<SearchResponse<PoleEmploiRegion>> {
    return this.distinctLocationChanges$.pipe(
      switchMap((query) => this.searchRegionsWebservice.getRegions(query)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private getSelectedLocation(): Observable<
    | {
        workplace: SelectedWorkplace;
        type: WorkplaceTypeEnum;
      }
    | undefined
  > {
    const searchParams = {
      restrictSearchableAttributes: ['code'],
      hitsPerPage: 1,
      typoTolerance: false,
    };
    return this.route.queryParamMap.pipe(
      map(
        (q) =>
          this.getLocation(q, WorkplaceTypeEnum.city) ??
          this.getLocation(q, WorkplaceTypeEnum.department) ??
          this.getLocation(q, WorkplaceTypeEnum.region)
      ),
      switchMap((location) =>
        location
          ? this.retrieveLocation(location.code, location.type, searchParams).pipe(
              map((response) => ({ workplace: response?.hits[0], type: location.type }))
            )
          : of(undefined)
      )
    );
  }

  private retrieveLocation(
    code: string,
    type: WorkplaceTypeEnum,
    searchParams?: SearchOptions
  ): Observable<SearchResponse<SelectedWorkplace>> {
    if (type === WorkplaceTypeEnum.city) {
      return this.searchCommunesWebservice.getCommunes(code, searchParams);
    }
    if (type === WorkplaceTypeEnum.department) {
      return this.searchDepartementsWebservice.getDepartments(code, searchParams);
    }
    return this.searchRegionsWebservice.getRegions(code, searchParams);
  }

  private getLocation(
    param: ParamMap,
    type: WorkplaceTypeEnum
  ): {
    type: WorkplaceTypeEnum;
    code: string;
  } | void {
    const code = param.get(type);
    if (code) {
      return { type, code };
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatSelectModule,
    NgUpperFirstPipeModule,
  ],
  declarations: [LocationSearchPoleEmploiComponent],
  exports: [LocationSearchPoleEmploiComponent],
})
export class SearchEngineLocationSearchPoleEmploiModule {}
